import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Alert,
  Button,
  Badge,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  Divider,
  Tooltip,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { useAuth } from "../context/AuthContext";
import EmployeeDashboard from "../components/dashboard/EmployeeDashboard";
import ManagerDashboard from "../components/dashboard/ManagerDashboard";
import HRDashboard from "../components/dashboard/HRDashboard";
import VCDashboard from "../components/dashboard/VCDashboard";
import AdminPanel from "./AdminPanel";
import {
  notificationService,
  UserNotification,
} from "../services/notificationService";

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [notificationAnchor, setNotificationAnchor] =
    useState<null | HTMLElement>(null);

  const unreadCount = useMemo(
    () =>
      notifications.filter(
        (notification) =>
          notification.is_read === 0 || notification.is_read === false,
      ).length,
    [notifications],
  );

  const loadNotifications = async () => {
    try {
      const data = await notificationService.getMyNotifications();
      setNotifications(data || []);
    } catch (error) {
      console.error("Failed to load notifications:", error);
    }
  };

  useEffect(() => {
    if (user) {
      loadNotifications();
    }
  }, [user?.id]);

  const handleOpenNotifications = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleCloseNotifications = () => {
    setNotificationAnchor(null);
  };

  const handleNotificationClick = async (notification: UserNotification) => {
    const isUnread =
      notification.is_read === 0 || notification.is_read === false;

    if (isUnread) {
      try {
        await notificationService.markAsRead(notification.id);
        setNotifications((prev) =>
          prev.map((item) =>
            item.id === notification.id ? { ...item, is_read: 1 } : item,
          ),
        );
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
      }
    }
  };

  if (!user) {
    return (
      <Container>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  const getWelcomeMessage = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  const getRoleDisplayName = (role: string) => {
    const roleNames: { [key: string]: string } = {
      employee: "Employee",
      staff: "Staff",
      manager: "Manager",
      hod: "Head of Department",
      dean: "Dean",
      establishment: "Establishment",
      vc: "Vice Chancellor",
      hr_manager: "HR Manager",
      admin: "Administrator",
      registrar: "Registrar",
    };
    return roleNames[role] || role;
  };

  const renderDashboard = () => {
    switch (user.role) {
      case "admin":
        return <AdminPanel />;
      case "hr_manager":
        return <HRDashboard />;
      case "vc":
        return <VCDashboard />;
      case "hod":
      case "dean":
      case "establishment":
      case "registrar":
      case "manager":
        return <ManagerDashboard />;
      case "employee":
      case "staff":
        return <EmployeeDashboard />;
      default:
        return <EmployeeDashboard />;
    }
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <img
            src="/logo.png"
            alt="University of Vavuniya Logo"
            style={{ maxWidth: "60px", height: "auto" }}
          />
          <Box>
            <Typography variant="h4" gutterBottom>
              {getWelcomeMessage()}, {user.name}!
            </Typography>
            <Typography variant="subtitle1" color="textSecondary" gutterBottom>
              {getRoleDisplayName(user.role)} • {user.department} •{" "}
              {user.designation}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Notifications">
            <IconButton color="inherit" onClick={handleOpenNotifications}>
              <Badge color="error" badgeContent={unreadCount}>
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<LogoutIcon />}
            onClick={() => logout()}
          >
            Logout
          </Button>
        </Box>
      </Box>

      <Menu
        anchorEl={notificationAnchor}
        open={Boolean(notificationAnchor)}
        onClose={handleCloseNotifications}
        PaperProps={{ sx: { width: 380, maxWidth: "95vw" } }}
      >
        <MenuItem disabled>
          <Typography variant="subtitle2">Notifications</Typography>
        </MenuItem>
        <Divider />
        {notifications.length === 0 && (
          <MenuItem disabled>
            <ListItemText primary="No notifications" />
          </MenuItem>
        )}
        {notifications.map((notification) => {
          const isUnread =
            notification.is_read === 0 || notification.is_read === false;
          return (
            <MenuItem
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              sx={{
                alignItems: "flex-start",
                whiteSpace: "normal",
                backgroundColor: isUnread ? "rgba(25, 118, 210, 0.08)" : "",
              }}
            >
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    fontWeight={isUnread ? "bold" : "normal"}
                  >
                    {notification.title}
                  </Typography>
                }
                secondary={
                  <>
                    <Typography variant="body2" color="text.secondary">
                      {notification.message}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(notification.created_at).toLocaleString()}
                    </Typography>
                  </>
                }
              />
            </MenuItem>
          );
        })}
      </Menu>

      {/* Role-specific welcome message */}
      {(user.role === "employee" || user.role === "staff") && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Welcome to your employee dashboard! Here you can apply for leaves and
          track your applications.
        </Alert>
      )}

      {(user.role === "hod" ||
        user.role === "dean" ||
        user.role === "manager" ||
        user.role === "vc" ||
        user.role === "establishment" ||
        user.role === "registrar") && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Welcome to your Leave Management dashboard!
        </Alert>
      )}

      {user.role === "hr_manager" && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Welcome to your HR dashboard! You have access to all employee data and
          leave management.
        </Alert>
      )}

      {user.role === "admin" && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Welcome to the Admin Panel! You can manage employees, departments,
          leave types, and system settings.
        </Alert>
      )}

      {/* Role-based dashboard */}
      {renderDashboard()}
    </Box>
  );
};

export default Dashboard;
